import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  combineLatest,
  distinctUntilChanged,
  EMPTY,
  map,
  Subject,
  switchMap,
  takeUntil,
  tap,
  timer,
} from 'rxjs';

import { loadHealth, selectHealthSpaces, selectIsHealthy } from 'src/app/stores/global';
import { loadSettings, selectSettingIsLoaded } from 'src/app/stores/settings';
import { HEALTH_CHECK_BEAT, HEALTH_CHECK_TIMEOUT } from 'src/environments/runtime';

import { TOAST_TYPES } from '../../utils/constants';
import { CustomSnackbarService } from '../custom-snackbar/custom-snackbar.service';

@Injectable({
  providedIn: 'root',
})
export class SystemReadyService implements OnDestroy {
  public readonly RETRY_WAIT = HEALTH_CHECK_BEAT;

  private started = false;
  private settingsRetryStarted = false;
  private destroy$ = new Subject<void>();
  private showWarningToasts = new Set<string>();
  readonly spaceStatus$ = this.store.select(selectHealthSpaces);

  readonly isReady$ = combineLatest([
    this.store.select(selectSettingIsLoaded),
    this.store.select(selectIsHealthy),
  ]).pipe(
    map(([settingsLoaded, healthy]) => settingsLoaded && healthy),
    distinctUntilChanged()
  );

  constructor(private store: Store, private customSnackbar: CustomSnackbarService) {}

  init() {
    if (this.started) return; // singleton use
    this.started = true;

    this.store.dispatch(loadHealth());

    this.initReady();
    this.initHealthWarnings();
  }

  initSettingsRetry(): void {
    if (this.settingsRetryStarted) return; // singleton use
    this.settingsRetryStarted = true;

    this.store
      .select(selectSettingIsLoaded)
      .pipe(
        switchMap((isLoaded) => {
          if (isLoaded) return EMPTY;

          // Starts one beat in, so it does not duplicate the boot dispatch.
          return timer(this.RETRY_WAIT, this.RETRY_WAIT).pipe(
            tap((attempt) => {
              console.warn(`[SystemReady] Settings retry ${attempt + 1}`);
              this.store.dispatch(loadSettings());
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initReady() {
    this.isReady$
      .pipe(
        switchMap((ready) => {
          if (ready) return EMPTY;

          return timer(0, this.RETRY_WAIT).pipe(
            tap((attempt) => {
              console.warn(`[SystemReady] Retry ${attempt + 1}`);
              this.store.dispatch(loadHealth());
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  private initHealthWarnings() {
    combineLatest([this.isReady$, this.spaceStatus$])
      .pipe(
        map(([ready, health]) => {
          if (!ready || health.totalSpaces === 0 || health.availableSpaces === 0)
            return 'NOT_READY';

          if (ready && health.totalSpaces > 0 && health.availableSpaces < health.totalSpaces) {
            return 'SPACES_NOT_READY';
          }

          return 'OK';
        }),
        distinctUntilChanged(),
        switchMap((state) => {
          if (state === 'OK') return EMPTY;

          return timer(Number(HEALTH_CHECK_TIMEOUT)).pipe(map(() => state));
        }),
        takeUntil(this.destroy$)
      )
      .subscribe((state) => {
        if (state === 'NOT_READY' && !this.showWarningToasts.has('NOT_READY')) {
          this.showWarningToasts.add('NOT_READY');
          console.warn('[SystemReady] something is not ready');
          this.customSnackbar.openSnackBar('systemReady.contactSupport', TOAST_TYPES.Error);
        }

        if (state === 'SPACES_NOT_READY' && !this.showWarningToasts.has('SPACES_NOT_READY')) {
          this.showWarningToasts.add('SPACES_NOT_READY');
          console.warn('[SystemReady] some spaces are not available');
          this.customSnackbar.openSnackBar('systemReady.spacesNotInitialized', TOAST_TYPES.Warning);
        }
      });
  }
}
